import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'
import type { AutomationMode, AutomationSettings, AutomationActionStatus } from '../types'

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string) {
  if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  return path.startsWith('/') ? path : `/${path}`
}

export class ApiError extends Error {
  code?: string
  constructor(message: string, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.code = code
  }
}

async function apiFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(buildUrl(path), {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })
  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new ApiError('Session expired')
    }
    const p = await res.json().catch(() => null)
    const details = Array.isArray(p?.error?.details) ? p.error.details : null
    const detailMessage = details?.length
      ? details.map((d: { field?: string; message?: string }) => d.message || d.field).join('; ')
      : null
    const message = detailMessage || p?.error?.message || p?.message || res.statusText || 'Request failed'
    throw new ApiError(message, p?.error?.code)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

export function getAutomationSettings(workspaceId: string): Promise<AutomationSettings> {
  return apiFetch('GET', `/api/workspaces/${workspaceId}/automation-settings`)
}

export function updateAutomationSettings(
  workspaceId: string,
  actionKey: string,
  mode: AutomationMode,
): Promise<AutomationSettings> {
  return apiFetch('PUT', `/api/workspaces/${workspaceId}/automation-settings`, { actions: { [actionKey]: mode } })
}

export async function getAutomationActionStatuses(workspaceId: string): Promise<Record<string, AutomationActionStatus>> {
  const { statuses } = await apiFetch<{ statuses: Record<string, AutomationActionStatus> }>(
    'GET',
    `/api/workspaces/${workspaceId}/automation-settings/status`,
  )
  return statuses
}
